import type { MouseEvent, PropsWithChildren, ReactNode } from 'react'
import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn, iconButtonClass, overlayClass, surfaceClass } from '../../utils/theme'

const FOCUSABLE_SELECTORS =
  'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  labelledBy?: string
  describedBy?: string
  footer?: ReactNode
  size?: ModalSize
  initialFocusRef?: React.RefObject<HTMLElement>
  hideCloseButton?: boolean
  className?: string
}

function getSizeClass(size: ModalSize = 'md') {
  switch (size) {
    case 'sm':
      return 'max-w-md'
    case 'lg':
      return 'max-w-3xl'
    case 'xl':
      return 'max-w-4xl'
    default:
      return 'max-w-2xl'
  }
}

export function Modal({
  isOpen,
  onClose,
  title,
  labelledBy,
  describedBy,
  footer,
  size = 'md',
  initialFocusRef,
  hideCloseButton,
  className,
  children,
}: PropsWithChildren<ModalProps>) {
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocusedElement = useRef<HTMLElement | null>(null)
  const generatedTitleId = useId()
  const titleId = labelledBy ?? (title ? `${generatedTitleId}-title` : undefined)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    previouslyFocusedElement.current = document.activeElement as HTMLElement | null

    const elementToFocus = initialFocusRef?.current
      ? initialFocusRef.current
      : (contentRef.current?.querySelector(FOCUSABLE_SELECTORS) as HTMLElement | null)

    elementToFocus?.focus({ preventScroll: true })

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }

      if (event.key === 'Tab') {
        const focusableElements = contentRef.current?.querySelectorAll<HTMLElement>(
          FOCUSABLE_SELECTORS,
        )
        if (!focusableElements || focusableElements.length === 0) {
          event.preventDefault()
          contentRef.current?.focus()
          return
        }

        const elements = Array.from(focusableElements)
        const first = elements[0]
        const last = elements[elements.length - 1]

        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault()
            last.focus()
          }
        } else if (document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }

    const contentNode = contentRef.current
    contentNode?.addEventListener('keydown', handleKeyDown)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      contentNode?.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
      previouslyFocusedElement.current?.focus({ preventScroll: true })
    }
  }, [initialFocusRef, isOpen, onClose])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    const timer = window.setTimeout(() => {
      contentRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [isOpen])

  const handleOverlayMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === overlayRef.current) {
      event.stopPropagation()
      onClose()
    }
  }

  const modalContent = !isOpen ? null : (
    <div
      ref={overlayRef}
      className={cn(
        overlayClass(),
        'z-50 flex items-center justify-center px-4 py-6 sm:px-6',
      )}
      onMouseDown={handleOverlayMouseDown}
      role="presentation"
    >
      <div
        ref={contentRef}
        tabIndex={-1}
        className={cn(
          surfaceClass('modal'),
          'relative w-full outline-none rounded-2xl',
          getSizeClass(size),
          'max-h-[90vh] overflow-hidden focus-visible:outline-none',
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-6 py-4 dark:border-neutral-800">
          {title ? (
            <h2 id={titleId} className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {title}
            </h2>
          ) : (
            <span id={titleId} className="sr-only">
              Modal
            </span>
          )}
          {!hideCloseButton && (
            <button
              type="button"
              onClick={onClose}
              className={iconButtonClass()}
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="max-h-[calc(90vh-8rem)] overflow-y-auto px-6 py-4">{children}</div>
        {footer ? (
          <div className="border-t border-neutral-200 px-6 py-4 dark:border-neutral-800">{footer}</div>
        ) : null}
      </div>
    </div>
  )

  return modalContent ? createPortal(modalContent, document.body) : null
}

export default Modal
